///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Unified sign-in / registration dialog. OAuth buttons use flow=continue:
/// the server signs in when the account exists and registers otherwise, so
/// there is a single entry point for both new and returning users. Passkey
/// sign-in is one button; passkey account creation lives behind a collapsed
/// disclosure since it needs name + email. Terms acceptance is a
/// "By continuing, you agree…" clickwrap notice recorded server-side at
/// registration. Glassmorphism styling throughout, except the OAuth buttons
/// which must keep their provider-mandated brand styles.
///

import { EmailVerificationDialog } from "@/components/dialogs/EmailVerificationDialog"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { Icons } from "@/components/ui/icons"
import { Input } from "@/components/ui/input"
import { ToastAction } from "@/components/ui/toast"
import { toast } from "@/components/ui/use-toast"
import Link from "@/components/utils/Link"
import { isIOSChrome } from "@/earthbrowser/utils/platform"
import { setAuthOpen, setDeviceLimitToken } from "@/redux/dialogSlice"
import { conversionRung } from "@/redux/engagementSlice"
import { useAppDispatch, useAppSelector, type RootState } from "@/redux/store"
import { track } from "@/utils/analytics"
import {
	useAuthenticateWithPasskeyMutation,
	useGetPasskeyAuthenticationOptionsMutation,
	useRegisterWithPasskeyCompleteMutation,
	useRegisterWithPasskeyInitMutation,
} from "@/redux/userApi"
import { getDeviceId } from "@/utils/deviceId"
import { isNativeApp } from "@/native"
import { isProduction, isTauriApp } from "@/utils/environment"
import { extractErrorMessage } from "@/utils/error-handler"
import { authenticateWithPasskey, isWebAuthnSupported, registerPasskey } from "@/utils/passkey"
import { validateRPID } from "@/utils/passkey-debug"
import { validateEmailAddress } from "@/utils/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"
import { ChevronDown, Loader2 } from "lucide-react"
import { useTheme } from "next-themes"
import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import { z } from "zod"

const createAccountFormSchema = z.object({
	firstName: z.string().min(1).max(32),
	lastName: z.string().min(1).max(32),
	email: z.string().email(),
})

export function AuthDialog() {
	const [searchParams, setSearchParams] = useSearchParams()
	const dispatch = useAppDispatch()
	const { resolvedTheme } = useTheme()
	const authOpen = useAppSelector((state: RootState) => state.dialog.authOpen)

	const [ getPasskeyAuthOptions ] = useGetPasskeyAuthenticationOptionsMutation()
	const [ authenticatePasskey, { isLoading: isPasskeyAuthLoading } ] = useAuthenticateWithPasskeyMutation()
	const [ registerWithPasskeyInit ] = useRegisterWithPasskeyInitMutation()
	const [ registerWithPasskeyComplete ] = useRegisterWithPasskeyCompleteMutation()

	const [ isAppleLoading, setIsAppleLoading ] = useState<boolean>(false)
	const [ isMicrosoftLoading, setIsMicrosoftLoading ] = useState<boolean>(false)
	const [ isGoogleLoading, setIsGoogleLoading ] = useState<boolean>(false)
	const [ isPasskeyRegisterLoading, setIsPasskeyRegisterLoading ] = useState<boolean>(false)
	const [ isPasskeySupported, setIsPasskeySupported ] = useState<boolean>(false)
	const [ isIOSChromeUser, setIsIOSChromeUser ] = useState<boolean>(false)
	const [ showCreateForm, setShowCreateForm ] = useState<boolean>(false)
	const [ turnstileStatus, setTurnstileStatus ] = useState<"success" | "error" | "expired" | "required">("required")
	const [ shakeTurnstile, setShakeTurnstile ] = useState(false)
	const turnstileContainerRef = useRef<HTMLDivElement>(null)
	const turnstileWidgetRef = useRef<TurnstileInstance | null>(null)
	const [ showResendVerification, setShowResendVerification ] = useState<boolean>(false)
	const [ resendEmail, setResendEmail ] = useState<string>("")
	const [ isResending, setIsResending ] = useState<boolean>(false)
	const [ showVerificationDialog, setShowVerificationDialog ] = useState<boolean>(false)
	const [ verificationEmail, setVerificationEmail ] = useState<string>("")

	// Check if passkeys are supported on mount
	useEffect(() => {
		setIsPasskeySupported(isWebAuthnSupported())
		setIsIOSChromeUser(isIOSChrome())
	}, [])

	// Funnel event: auth intent (pairs with account_created in userApi)
	const rung = useAppSelector(state => conversionRung(state.engagement))
	useEffect(() => {
		if (authOpen) {
			track("auth_dialog_opened", { rung })
		}
	}, [authOpen, rung])

	// Open from URL params (?login= / ?register= from OAuth error redirects
	// and deep links) and surface any error as a toast. ?deviceLimit= is the
	// eviction token from a device-limit-blocked OAuth sign-in: it opens the
	// DeviceLimitDialog so the user can sign a device out and retry.
	useEffect(() => {
		const error = searchParams?.get('error')
		const shouldOpen = searchParams?.get('login') || searchParams?.get('register')
		const deviceLimit = searchParams?.get('deviceLimit')

		if (!error && !shouldOpen && !deviceLimit) return

		if (error) {
			toast({
				title: "Sign in failed",
				description: error,
				variant: "destructive"
			})
		}

		if (shouldOpen) {
			dispatch(setAuthOpen(true))
		}

		if (deviceLimit) {
			dispatch(setDeviceLimitToken(deviceLimit))
		}

		setSearchParams((prev) => {
			prev.delete('error')
			prev.delete('login')
			prev.delete('register')
			prev.delete('deviceLimit')
			return prev
		}, { replace: true })
	}, [searchParams, dispatch, setSearchParams])

	const form = useForm<z.infer<typeof createAccountFormSchema>>({
		resolver: zodResolver(createAccountFormSchema),
		defaultValues: {
			firstName: "",
			lastName: "",
			email: "",
		},
	})

	// Watch form fields to trigger re-renders on input changes
	const firstName = form.watch("firstName")
	const lastName = form.watch("lastName")
	const email = form.watch("email")

	function isLoading() {
		return isPasskeyAuthLoading || isPasskeyRegisterLoading || isGoogleLoading || isAppleLoading || isMicrosoftLoading
	}

	const turnstileValid = useCallback(() => {
		return (isProduction() && !isTauriApp()) ? turnstileStatus === "success" : true
	}, [turnstileStatus])

	const highlightTurnstile = useCallback(() => {
		if (turnstileValid()) return
		turnstileContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
		setShakeTurnstile(true)
		setTimeout(() => setShakeTurnstile(false), 1000)
	}, [turnstileValid])

	function nameAndEmailValid() {
		if (!firstName || !lastName || !email) return false
		if (firstName.length < 1 || firstName.length > 32) return false
		if (lastName.length < 1 || lastName.length > 32) return false
		return validateEmailAddress(email)
	}

	/**
	 * Unified OAuth flow: the server signs in when the account exists and
	 * registers a new account otherwise. The device id is always sent (via
	 * the oauth-url cookie channel, since the callback is a provider
	 * redirect that can't carry our headers).
	 */
	async function oauthContinue(provider: "apple" | "microsoft" | "google", setLoading: (v: boolean) => void) {
		setLoading(true)

		try {
			const deviceId = getDeviceId()
			const response = await fetch(`${import.meta.env.VITE_API_URL}/api/webhooks/${provider}-oauth-url?flow=continue&deviceFingerprint=${encodeURIComponent(deviceId)}`, {
				credentials: 'include'
			})

			if (!response.ok) {
				throw new Error(`Server returned ${response.status}`)
			}

			const data = await response.json() as { url: string }
			if (!data.url) {
				throw new Error("No OAuth URL received")
			}

			window.location.href = data.url
		} catch (error) {
			setLoading(false)
			toast({
				title: "Sign In Error",
				variant: "destructive",
				description: error instanceof Error ? error.message : "Failed to start sign in",
			})
		}
	}

	async function passkeySignIn() {
		const freshToken = turnstileWidgetRef.current?.getResponse() ?? ""
		if (isProduction() && !isTauriApp() && !freshToken) {
			toast({
				title: "Captcha Error",
				variant: "destructive",
				description: "Please complete the captcha challenge",
			})
			return
		}

		try {
			// Get authentication options from server
			const optionsResult = await getPasskeyAuthOptions()
			if (!optionsResult.data) {
				console.error("Failed to get authentication options:", optionsResult)
				const errorMessage = optionsResult.error
					? (typeof optionsResult.error === 'object' && 'data' in optionsResult.error
						? JSON.stringify(optionsResult.error.data)
						: JSON.stringify(optionsResult.error))
					: "No response from server"
				throw new Error(`Failed to get authentication options: ${errorMessage}`)
			}

			// Validate RPID before attempting authentication
			const validation = validateRPID(optionsResult.data.rpId || "")
			if (!validation.valid) {
				console.error("RPID Validation Failed:", validation)
				throw new Error(validation.message + (validation.suggestion ? `\n\nSuggestion: ${validation.suggestion}` : ""))
			}

			// Perform WebAuthn authentication
			const credential = await authenticateWithPasskey(optionsResult.data)

			// Send credential to server for verification
			const result = await authenticatePasskey({
				credential,
				turnstileToken: (isProduction() && !isTauriApp()) ? freshToken : undefined,
			})

			if (result.data) {
				dispatch(setAuthOpen(false))
				// Page will refresh automatically via React Router
			} else if (result.error) {
				// Check if it's an unverified email error (403)
				const errorData = result.error as { status: number, data: { message: string, evictionToken?: string } }
				const status = errorData?.status
				const message = errorData?.data?.message || ("error" in result.error ? String(result.error.error) : "Authentication failed")

				if (status === 409 && errorData?.data?.evictionToken) {
					// Device seat limit reached: open the eviction dialog so
					// the user can sign a device out and retry
					dispatch(setAuthOpen(false))
					dispatch(setDeviceLimitToken(errorData.data.evictionToken))
				} else if (status === 403 && message.includes("verify your email")) {
					toast({
						title: "Email Not Verified",
						variant: "destructive",
						description: message,
						duration: 10000,
						action: (
							<ToastAction
								altText="Resend verification email"
								onClick={() => setShowResendVerification(true)}
							>
								Resend Email
							</ToastAction>
						),
					})
				} else {
					toast({
						title: "Passkey Sign In Failed",
						variant: "destructive",
						description: message,
					})
				}
			}
		} catch (error) {
			console.error("Passkey sign in error:", error)
			toast({
				title: "Passkey Sign In Failed",
				variant: "destructive",
				description: error instanceof Error ? error.message : "Authentication failed",
			})
		}
	}

	async function onCreateWithPasskey() {
		// Get a fresh token directly from the widget to avoid stale state on mobile
		const freshToken = turnstileWidgetRef.current?.getResponse() ?? ""
		if (isProduction() && !isTauriApp() && !freshToken) {
			toast({
				title: "Captcha Error",
				variant: "destructive",
				description: "Please complete the captcha challenge",
			})
			return
		}

		const { firstName, lastName, email } = form.getValues()
		if (!firstName || !lastName || !email) {
			toast({
				title: "Missing Information",
				variant: "destructive",
				description: "Please fill in all fields",
			})
			return
		}

		setIsPasskeyRegisterLoading(true)

		try {
			// Step 1: Initialize registration (creates user account). The
			// device id is included for registration abuse analytics.
			const initResult = await registerWithPasskeyInit({
				firstName,
				lastName,
				email,
				deviceFingerprint: getDeviceId(),
				turnstileToken: freshToken,
			})

			if ("error" in initResult && initResult.error) {
				throw new Error(extractErrorMessage(initResult.error, "Failed to initialize passkey registration"))
			}

			if (!initResult.data) {
				throw new Error("No registration options received")
			}

			// Step 2: Create passkey with WebAuthn
			const credential = await registerPasskey(initResult.data)

			// Step 3: Complete registration (stores passkey and sends verification email)
			const completeResult = await registerWithPasskeyComplete({ credential })

			if ("error" in completeResult && completeResult.error) {
				throw new Error(extractErrorMessage(completeResult.error, "Failed to complete passkey registration"))
			}

			if (completeResult.data) {
				dispatch(setAuthOpen(false))

				// Show email verification dialog
				setVerificationEmail(completeResult.data.email)
				setShowVerificationDialog(true)

				toast({
					title: "Registration Successful!",
					description: "Please check your email to verify your account and complete registration.",
					duration: 8000,
				})
			}
		} catch (error) {
			console.error("Passkey registration error:", error)
			toast({
				title: "Registration Failed",
				variant: "destructive",
				description: error instanceof Error ? error.message : "Unable to register with passkey",
			})
		} finally {
			setIsPasskeyRegisterLoading(false)
		}
	}

	async function handleResendVerification() {
		if (!resendEmail?.includes('@')) {
			toast({
				title: "Invalid Email",
				variant: "destructive",
				description: "Please enter a valid email address",
			})
			return
		}

		setIsResending(true)

		try {
			const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/resend-verification-email`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ email: resendEmail }),
			})

			if (!response.ok) {
				const error = await response.json() as { message: string }
				throw new Error(error.message || 'Failed to resend verification email')
			}

			const result = await response.json() as { message: string }

			toast({
				title: "Verification Email Sent",
				description: result.message,
			})

			setShowResendVerification(false)
			setResendEmail("")
		} catch (error) {
			console.error("Resend verification error:", error)
			toast({
				title: "Failed to Resend",
				variant: "destructive",
				description: error instanceof Error ? error.message : "Unable to resend verification email",
			})
		} finally {
			setIsResending(false)
		}
	}

	// Provider brand button styles (mandated by each provider's sign-in
	// branding guidelines — intentionally NOT glassmorphism)
	const googleButtonClass = resolvedTheme === "dark"
		? "w-full bg-white hover:bg-white/90 text-[#1F1F1F] border border-[#747775]"
		: "w-full bg-[#131314] hover:bg-[#131314]/90 text-[#E3E3E3] border border-[#8E918F]"
	const microsoftButtonClass = resolvedTheme === "dark"
		? "w-full bg-white hover:bg-white/90 text-[#5E5E5E] border border-[#8A8A8A]"
		: "w-full bg-[#2F2F2F] hover:bg-[#2F2F2F]/90 text-white border border-[#8A8A8A]"
	const appleButtonClass = resolvedTheme === "dark"
		? "w-full bg-white hover:bg-white/90 text-black border-0"
		: "w-full bg-black hover:bg-black/90 text-white border-0"

	return (
		<>
		<Dialog open={authOpen} onOpenChange={(open) => dispatch(setAuthOpen(open))}>
		<DialogContent
			className="grid gap-4 bg-black/40 dark:bg-black/40 backdrop-blur-xl border border-white/20 dark:border-white/20 text-white shadow-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto"
			aria-describedby=""
			onInteractOutside={(e) => {
				if (isLoading()) {
					e.preventDefault()
				}
			}}
		>
			<DialogHeader className="space-y-1">
				<DialogTitle className="text-2xl text-white">Sign in or create account</DialogTitle>
				<DialogDescription className="text-white/60">
					One click with any provider — new accounts are created automatically
				</DialogDescription>
			</DialogHeader>

			{/* OAuth providers: unified continue flow */}
			<div className="grid grid-cols-1 gap-3" onPointerDown={highlightTurnstile}>
				{/* Google - Following Google's Branding Guidelines */}
				<Button
					id="google-continue"
					disabled={isLoading() || !turnstileValid()}
					onClick={() => oauthContinue("google", setIsGoogleLoading)}
					className={googleButtonClass}
				>
					{isGoogleLoading ? (
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
					) : (
						<Icons.googleLogo className="mr-2 h-5 w-5" />
					)}
					Continue with Google
				</Button>
				{/* Microsoft - Following Microsoft's Branding Guidelines. Web only:
				    the mobile apps offer Apple, Google and passkeys
				    (documentation/mobile/NATIVE_AUTH_PLAN.md) */}
				{!isNativeApp() && (
					<Button
						id="microsoft-continue"
						disabled={isLoading() || !turnstileValid()}
						onClick={() => oauthContinue("microsoft", setIsMicrosoftLoading)}
						className={microsoftButtonClass}
					>
						{isMicrosoftLoading ? (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						) : (
							<Icons.microsoft className="mr-2 h-5 w-5" />
						)}
						Continue with Microsoft
					</Button>
				)}
				{/* Apple - Following Apple's Design Guidelines */}
				<Button
					id="apple-continue"
					disabled={isLoading() || !turnstileValid()}
					onClick={() => oauthContinue("apple", setIsAppleLoading)}
					className={appleButtonClass}
				>
					{isAppleLoading ? (
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
					) : (
						<Icons.apple className="mr-2 h-5 w-5" />
					)}
					Continue with Apple
				</Button>
			</div>

			{isPasskeySupported && !isIOSChromeUser ? (
				<>
				<div className="relative">
					<div className="absolute inset-0 flex items-center">
						<span className="w-full border-t border-white/15" />
					</div>
					<div className="relative flex justify-center text-xs uppercase">
						<span className="bg-transparent px-2 text-white/50 backdrop-blur-sm">
							OR
						</span>
					</div>
				</div>

				{/* Passkey sign-in for existing passkey accounts */}
				<div className="grid gap-2" onPointerDown={highlightTurnstile}>
					<Button
						disabled={isLoading() || !turnstileValid()}
						onClick={() => passkeySignIn()}
						className="w-full rounded-full border border-white/20 bg-white/10 backdrop-blur-md text-white shadow-lg transition-all duration-300 hover:scale-105 hover:bg-white/20 hover:shadow-xl disabled:hover:scale-100"
					>
						{isPasskeyAuthLoading ? (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						) : (
							<Icons.key className="mr-2 h-4 w-4" />
						)}
						<span className="font-medium">Sign in with Passkey</span>
					</Button>
				</div>

				{/* Collapsed passkey account creation (needs name + email) */}
				<div className="rounded-xl border border-white/15 bg-white/5 backdrop-blur-md overflow-hidden">
					<button
						type="button"
						onClick={() => setShowCreateForm(!showCreateForm)}
						aria-expanded={showCreateForm}
						className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
					>
						<span>New here? Create an account with a passkey</span>
						<ChevronDown className={`h-4 w-4 transition-transform duration-300 ${showCreateForm ? 'rotate-180' : ''}`} />
					</button>

					{showCreateForm && (
						<Form {...form}>
							<form onSubmit={form.handleSubmit(onCreateWithPasskey)} className="space-y-3 px-4 pb-4 pt-1">
								<div className="grid grid-cols-2 gap-4">
									<FormField
										control={form.control}
										name="firstName"
										disabled={isLoading()}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-white/80">First Name</FormLabel>
												<FormControl>
													<Input placeholder="First Name" autoComplete="given-name" className="bg-white/5 border-white/20 text-white placeholder:text-white/40" {...field} />
												</FormControl>
											</FormItem>
									)}/>
									<FormField
										control={form.control}
										name="lastName"
										disabled={isLoading()}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-white/80">Last Name</FormLabel>
												<FormControl>
													<Input placeholder="Last Name" autoComplete="family-name" className="bg-white/5 border-white/20 text-white placeholder:text-white/40" {...field} />
												</FormControl>
											</FormItem>
									)}/>
								</div>
								<FormField
									control={form.control}
									name="email"
									disabled={isLoading()}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-white/80">Email</FormLabel>
											<FormControl>
												<Input
													type="email"
													placeholder="example@email.com"
													autoComplete="email"
													className="bg-white/5 border-white/20 text-white placeholder:text-white/40" {...field} />
											</FormControl>
										</FormItem>
									)}/>

								<div onPointerDown={highlightTurnstile}>
									<Button
										type="submit"
										disabled={isLoading() || !turnstileValid() || !nameAndEmailValid()}
										className="w-full rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white disabled:hover:scale-100"
									>
										{isPasskeyRegisterLoading ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<Icons.key className="mr-2 h-4 w-4" />
										)}
										<span className="font-medium">Create account with Passkey</span>
									</Button>
								</div>
								<p className="text-xs text-center text-white/50">
									No password needed — use Face ID, Touch ID, or Windows Hello
								</p>
							</form>
						</Form>
					)}
				</div>
				</>
			) : isIOSChromeUser ? (
				<div className="text-center py-4 px-2 rounded-xl bg-amber-500/10 backdrop-blur-md border border-amber-500/20">
					<p className="text-sm text-white/70">
						Passkeys are not supported in Chrome on iOS.
					</p>
					<p className="text-xs text-white/50 mt-2">
						Please use Safari, or continue with Google, Apple, or Microsoft above.
					</p>
				</div>
			) : null}

			{/* Resend Verification Email Section - only shown after email verification error */}
			{showResendVerification && (
				<div className="space-y-3 pt-2 rounded-xl border border-white/15 bg-white/5 backdrop-blur-md p-4">
					<div className="text-center">
						<p className="text-sm font-medium mb-2 text-white">Resend Verification Email</p>
						<p className="text-xs text-white/50 mb-3">
							Enter your email address to receive a new verification link
						</p>
					</div>
					<div className="flex gap-2">
						<input
							type="email"
							placeholder="your.email@example.com"
							value={resendEmail}
							onChange={(e) => setResendEmail(e.target.value)}
							autoComplete="off"
							data-1p-ignore="true"
							data-lpignore="true"
							data-form-type="other"
							className="flex-1 px-3 py-2 text-sm rounded-md border border-white/20 bg-white/5 text-white placeholder:text-white/40"
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									void handleResendVerification()
								}
							}}
						/>
						<Button
							onClick={() => void handleResendVerification()}
							disabled={isResending || !resendEmail}
							size="sm"
						>
							{isResending ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								"Send"
							)}
						</Button>
					</div>
					<button
						onClick={() => {
							setShowResendVerification(false)
							setResendEmail("")
						}}
						className="text-xs text-white/50 hover:text-white underline w-full text-center"
					>
						Cancel
					</button>
				</div>
			)}

			{/* Clickwrap notice: acceptance is recorded server-side at registration */}
			<p className="text-xs text-center text-white/50 leading-relaxed">
				By continuing, you agree to the{" "}
				<Link
					href="/terms-and-conditions"
					target="_blank"
					rel="noopener noreferrer"
					className="text-blue-400 hover:text-blue-300 underline"
				>
					Terms and Conditions
				</Link>
				{" "}and{" "}
				<Link
					href="/privacy"
					target="_blank"
					rel="noopener noreferrer"
					className="text-blue-400 hover:text-blue-300 underline"
				>
					Privacy Policy
				</Link>
				. We store a random identifier on this device to recognize it and prevent fraud.{" "}
				<Link
					href="/privacy#device-fingerprinting"
					target="_blank"
					rel="noopener noreferrer"
					className="text-blue-400 hover:text-blue-300 underline"
				>
					Learn more
				</Link>
			</p>

			{/* Turnstile Captcha */}
			{(isProduction() && !isTauriApp()) && (
				<div
					ref={turnstileContainerRef}
					className={`flex flex-col items-center gap-2 transition-all duration-300 ${shakeTurnstile ? 'animate-shake' : ''} ${turnstileStatus !== 'success' ? 'ring-2 ring-amber-500/60 rounded-lg p-2' : ''}`}
				>
					{turnstileStatus !== 'success' && (
						<p className="text-xs text-amber-500 font-medium">Please complete the captcha to continue</p>
					)}
					<Turnstile
						ref={turnstileWidgetRef}
						siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY!}
						onError={() => setTurnstileStatus('error')}
						onExpire={() => setTurnstileStatus('expired')}
						options={{
							action: 'submit-form',
							theme: 'dark',
							size: 'flexible',
							refreshExpired: 'auto',
							retry: 'auto',
						}}
						onSuccess={() => {
							setTurnstileStatus('success')
						}}
					/>
				</div>
			)}
		</DialogContent>
		</Dialog>

		<EmailVerificationDialog
			open={showVerificationDialog}
			onClose={() => setShowVerificationDialog(false)}
			email={verificationEmail}
		/>
		</>
	)
}
